<script>
import UtilsMixin from '@mixins/utilsMixin';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2.vue';
import moment from 'moment';
import threadMixin from '@mixins/const/threadMixin';
import inquiryService from '@services/inquiryService';
import AkButtonValidationV2 from '@components/v2/general/AkButtonValidationV2.vue';
import MessageMixin from '@mixins/messageMixin';
import specialOfferService from '@services/specialOfferService';
import reservationService from '@services/reservationService';
import countdownMixin from '@mixins/const/countdownMixin';
import AkDialogHeadless from '@components/general/AkDialogHeadless.vue';
import AkDropdown from '@components/v2/input/AkDropdown.vue';
import AkCalendar from '@components/v2/input/AkCalendar.vue';
import AkInputNumber from '@components/v2/input/AkInputNumber.vue';
import PermanentMessage from '@views/onboarding/v4/component/PermanentMessage.vue';
import AkInputTextArea from '@components/v2/input/AkInputTextArea.vue';
import useVuelidate from '@vuelidate/core';
import {required} from '@vuelidate/validators';
import Spinner from '@components/v2/general/Spinner.vue';
import AkSelect from '@components/v2/input/AkSelect.vue';
import AkOptionSelect from '@components/v2/input/AkOptionSelect.vue';
import AkYesNoButton from '@components/v2/input/AkYesNoButton.vue';
import AkDialogConfirm from '@components/general/AkDialogConfirm.vue';
import AkTime from '@components/v2/input/AkTime.vue';
import permissionsMixinComputed from '@mixins/permissionsMixinComputed';

export default {
  name: 'ThreadTypeCard',
  components: {
    AkTime,
    AkDialogConfirm,
    AkYesNoButton,
    AkOptionSelect,
    AkSelect,
    Spinner,
    AkInputTextArea,
    PermanentMessage,
    AkDropdown,
    AkCalendar,
    AkInputNumber,
    AkDialogHeadless,
    AkButtonValidationV2,
    AkButtonActionV2,
  },
  mixins: [UtilsMixin, threadMixin, MessageMixin, countdownMixin, permissionsMixinComputed],
  emits: ['update'],
  inject: ['threadActionOrigin'],
  setup() {
    return {v$: useVuelidate()};
  },
  validations() {
    return {
      denyReasons: {
        key: {required},
        otaMessage: {required},
        guestMessage: {required},
      },
      specialOfferToCreate: {
        dateRange: {required},
        nbGuest: {required},
        totalPrice: {required},
      },
    };
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      priceDetailsOpen: false,
      specialOfferToCreate: {dateRange: null, nbGuest: null, totalPrice: null},
      specialOfferStep: 1,
      specialOfferLoading: false,
      denyReasons: {key: '', otaMessage: '', guestMessage: ''},
      reservationQueryDenyStep: 1,
      timesToEdit: {checkin: null, checkout: null},
    };
  },
  computed: {
    thread: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    details() {
      return this.thread.typeDetails;
    },
    threadType() {
      return this.details.type;
    },
    isReservation() {
      return this.isReservationConfirmed || this.isReservationCancelled;
    },
    isReservationQueryInactive() {
      return this.isReservationRetracted || this.isLiveReservationExpired;
    },
    // Live client-side expiry: threadType is a static snapshot from the backend and only
    // flips to *_timeout on next fetch. These override it the instant the countdown hits zero,
    // so action buttons disappear without waiting for a refetch.
    isLiveReservationExpired() {
      return this.isReservationExpired || (this.isReservationQuery && this.isExpired(this.details.expiry));
    },
    isLiveInquiryExpired() {
      return this.isInquiryExpired || (this.isInquiry && this.isExpired(this.details.expiry));
    },
    isLiveSpecialOfferExpired() {
      return this.isSpecialOfferActive && this.isExpired(this.details.expiry);
    },
    isReservationQueryLive() {
      return this.isReservationQuery && !this.isLiveReservationExpired;
    },
    isInquiryLive() {
      return this.isInquiry && !this.isLiveInquiryExpired;
    },
    isSpecialOfferActiveLive() {
      return this.isSpecialOfferActive && !this.isLiveSpecialOfferExpired;
    },
    timeLeft() {
      if ((!this.isReservationQuery && !this.isInquiry && !this.isSpecialOfferActive) || !this.details.expiry) return;
      return this.formatCountdown(this.details.expiry);
    },
    formatDateExpiry() {
      const date = moment.utc(this.details.expiry).local();
      return `Expire ${this.formatResaDate(new Date(this.details.expiry))} à ${date.format('HH[h]mm')}`;
    },
    isDisabled() {
      return (
        this.isReservationCancelled ||
        this.isReservationDenied ||
        this.isReservationRetracted ||
        this.isLiveReservationExpired ||
        this.isLiveInquiryExpired ||
        this.isInquiryDeclined ||
        this.isSpecialOfferVoided
      );
    },
    checkinMinDate() {
      if (!this.details.prevReservationCheckout || !this.details.dateCheckin) return undefined;
      const prevCheckout = moment(this.details.prevReservationCheckout);
      if (!prevCheckout.isSame(moment(this.details.dateCheckin), 'day')) return undefined;
      return prevCheckout.add('minute', 1).toDate();
    },
    checkoutMaxDate() {
      if (!this.details.nextReservationCheckin || !this.details.dateCheckout) return undefined;
      const nextCheckin = moment(this.details.nextReservationCheckin);
      if (!nextCheckin.isSame(moment(this.details.dateCheckout), 'day')) return undefined;
      return nextCheckin.subtract('minute', 1).toDate();
    },
    resaDatesFormatted() {
      return {
        checkin: this.formatResaDate(this.details.dateCheckin),
        checkout: this.formatResaDate(this.details.dateCheckout),
      };
    },
    specialOfferNbGuestOptions() {
      return Array.from({length: this.specialOfferToCreate.nbPeopleMax}, (_, i) => i + 1).map((n) => ({
        label: n,
        value: n,
      }));
    },
    clientName() {
      return this.thread.guest.firstName;
    },
    accommodationName() {
      return this.thread.accommodation.name;
    },
    nbPeople() {
      return Number(this.details.nbAdults) + Number(this.details.nbChildren) + Number(this.details.nbPets);
    },
    nbChildrenAndBabies() {
      return (this.details.nbChildren || 0) + (this.details.nbBabies || 0);
    },
    canEditTimes() {
      return (
        Boolean(this.details.inquiryId || this.details.specialOfferId || this.details.reservationId) &&
        !this.isDisabled &&
        this.canAct
      );
    },
    canAct() {
      return Boolean(this.thread.accommodation?.canAct);
    },
  },
  watch: {
    specialOfferStep() {
      this.resetDialogScroll('specialOfferDialog');
    },
    specialOfferLoading() {
      this.resetDialogScroll('specialOfferDialog');
    },
    reservationQueryDenyStep() {
      this.resetDialogScroll('denyReservationQueryDialog');
    },
  },
  methods: {
    resetDialogScroll(dialogRef) {
      this.$nextTick(() => this.$refs[dialogRef]?.resetScroll());
    },
    formatResaDate(date) {
      if (!date) return '';
      const momentDate = moment(date);
      if (momentDate.isSame(moment(), 'day')) return "aujourd'hui";
      if (momentDate.isSame(moment().subtract(1, 'day'), 'day')) return 'hier';
      if (momentDate.isSame(moment().add(1, 'day'), 'day')) return 'demain';
      return this.displayDateFullMonth(date);
    },
    formatAdjacentReservationDateTime(date) {
      if (!date) return '';
      const momentDate = moment(date);
      const time = this.displayTime(date);
      if (momentDate.isSame(moment(), 'day')) return `aujourd'hui à ${time}`;
      if (momentDate.isSame(moment().subtract(1, 'day'), 'day')) return `hier à ${time}`;
      if (momentDate.isSame(moment().add(1, 'day'), 'day')) return `demain à ${time}`;
      const dayMonth = momentDate.isSame(moment(), 'year')
        ? momentDate.format('DD MMMM')
        : momentDate.format('DD MMMM YYYY');
      return `${dayMonth}`;
    },
    preapprove() {
      inquiryService
        .preapproveThread(this.details.inquiryId, this.threadActionOrigin)
        .then((details) => {
          details.threadId = this.thread.id;
          this.$emit('update', details);
          this.$refs.preapproveBtn.init();
          this.success("Demande d'information préapprouvée");
        })
        .catch((e) => {
          this.$refs.preapproveBtn.init();
          this.addError("Erreur lors de la préapprobation de la demande d'informations : " + e);
        });
    },
    withdrawSpecialOffer() {
      specialOfferService
        .withdraw(this.details.specialOfferId, this.threadActionOrigin)
        .then((details) => {
          details.threadId = this.thread.id;
          this.$emit('update', details);
          this.$refs.withdrawSpecialOfferBtn.init();
          this.success('Offre spéciale annulée');
        })
        .catch((e) => {
          this.$refs.withdrawSpecialOfferBtn.init();
          this.addError("Erreur lors de l'annulation de l'offre spéciale : " + e);
        });
    },
    initSpecialOfferFromInquiry() {
      const checkin = moment(this.details.dateCheckin);
      const checkout = moment(this.details.dateCheckout);
      const isSameMonth = checkin.isSame(checkout, 'month');

      this.specialOfferToCreate = {
        dateRange: [checkin.toDate(), checkout.toDate()],
        datesUnavailable: [],
        nbPeopleMax: null,
        nbGuest: this.nbPeople,
        totalPrice: null,
        threadId: this.thread.id,
        datesPlaceholder: checkin.format(isSameMonth ? 'DD' : 'DD MMMM') + ' - ' + checkout.format('DD MMMM'),
      };
      this.specialOfferStep = 1;
      this.specialOfferLoading = true;
      this.$refs.specialOfferDialog.show();

      specialOfferService
        .getAvailability(this.thread.accommodation.id)
        .then((res) => {
          this.specialOfferToCreate.datesUnavailable = res.datesUnavailable.map((v) => new Date(v));
          this.specialOfferToCreate.nbPeopleMax = res.nbPeopleMax;
          this.specialOfferToCreate.nbGuest = Math.min(this.specialOfferToCreate.nbGuest, res.nbPeopleMax);
        })
        .catch((e) => {
          this.addError('Erreur lors de la récupération des disponibilités : ' + e);
          this.$refs.specialOfferDialog.cancel();
        })
        .finally(() => {
          this.specialOfferLoading = false;
        });
    },
    sendSpecialOffer() {
      this.v$.$touch();
      if (this.v$.specialOfferToCreate.$error) return;

      if (this.isExpired(this.details.expiry)) {
        this.closeSpecialOfferDialog();
        return;
      }

      this.specialOfferToCreate.dateStart = this.specialOfferToCreate.dateRange[0];
      this.specialOfferToCreate.dateEnd = this.specialOfferToCreate.dateRange[1];
      this.specialOfferToCreate.origin = this.threadActionOrigin;

      specialOfferService
        .create(this.specialOfferToCreate)
        .then((details) => {
          details.threadId = this.thread.id;
          this.$emit('update', details);
          this.specialOfferStep++;
        })
        .catch((e) => {
          this.addError("Erreur lors de la création de l'offre spéciale : " + e);
        });
    },
    closeSpecialOfferDialog() {
      this.$refs.specialOfferDialog.cancel();
    },
    initApproveReservationQuery() {
      this.$refs.confirmReservationQueryDialog
        .confirm()
        .then(this.approveReservationQuery)
        .catch(() => {
          this.$refs.resaQueryApproveBtn.init();
        });
    },
    approveReservationQuery() {
      if (this.isExpired(this.details.expiry)) {
        this.$refs.resaQueryApproveBtn.init();
        return;
      }

      reservationService
        .approveReservationRequest(this.details.reservationId, this.threadActionOrigin)
        .then((details) => {
          details.threadId = this.thread.id;
          this.$emit('update', details);
          this.$refs.resaQueryApproveBtn.init();
          this.success('Demande de réservation acceptée');
        })
        .catch((e) => {
          this.$refs.resaQueryApproveBtn.init();
          this.addError("Erreur lors de l'acceptation de la demande de réservation : " + this.msgForErr(this.$t, e));
        });
    },
    initReservationQueryDeny() {
      this.denyReasons = this.buildDenyReasons();
      this.reservationQueryDenyStep = 1;
      this.$refs.denyReservationQueryDialog.show();
    },
    buildDenyReasons() {
      const greeting = this.clientName ? `Bonjour ${this.clientName},` : 'Bonjour,';
      return {
        key: '',
        otaMessage: [
          'Bonjour,',
          `Nous n’avons malheureusement pas pu donner suite à la demande n°${this.details.reservationLocalizer} pour le logement ${this.accommodationName}.`,
          'Le logement n’était pas en mesure d’accueillir le voyageur sur les dates concernées.',
          'Cordialement,',
          'L’équipe Guestadom',
        ].join('\n\n'),
        guestMessage: [
          greeting,
          `Nous sommes au regret de vous informer que nous ne pouvons pas donner suite à votre demande pour le logement ${this.accommodationName}.`,
          'Nous aurions été ravis de vous accueillir. Le logement n’est pas en mesure de vous recevoir sur les dates que vous avez indiquées.',
          'Nous nous excusons de la gêne occasionnée.',
          'Au plaisir de vous accueillir à l’avenir.',
          'L’équipe Guestadom',
        ].join('\n\n'),
      };
    },
    denyReservationQuery() {
      if (this.isExpired(this.details.expiry)) {
        this.closeReservationQueryDeny();
        return;
      }

      reservationService
        .denyReservationRequest(this.details.reservationId, {...this.denyReasons, origin: this.threadActionOrigin})
        .then((details) => {
          details.threadId = this.thread.id;
          this.$emit('update', details);
          this.reservationQueryDenyStep++;
          this.success('Demande de réservation refusée');
        })
        .catch((e) => {
          this.$refs.denyReservationQuerySubmitBtn.init();
          this.addError('Erreur lors du refus de la demande de réservation : ' + this.msgForErr(this.$t, e));
        });
    },
    nextReservationQueryDenyStep() {
      this.v$.$touch();
      if (this.v$.denyReasons.key.$error) return;
      this.reservationQueryDenyStep++;
    },
    nextReservationQueryDenyStepFromMessage() {
      this.v$.$touch();
      if (this.v$.denyReasons.otaMessage.$error) return;
      this.reservationQueryDenyStep++;
    },
    reservationQueryDenyBack() {
      if (this.reservationQueryDenyStep > 1) this.reservationQueryDenyStep--;
      else this.closeReservationQueryDeny();
    },
    closeReservationQueryDeny() {
      this.$refs.denyReservationQueryDialog.cancel();
    },
    openEditTimes() {
      this.timesToEdit = {
        checkin: this.details.dateCheckin ? new Date(this.details.dateCheckin) : null,
        checkout: this.details.dateCheckout ? new Date(this.details.dateCheckout) : null,
      };
      this.$refs.editTimesDialog.show();
    },
    saveTimes() {
      const times = {
        checkinTime: this.timesToEdit.checkin ? this.displayTime(this.timesToEdit.checkin) : null,
        checkoutTime: this.timesToEdit.checkout ? this.displayTime(this.timesToEdit.checkout) : null,
        origin: this.threadActionOrigin,
      };

      let request;
      if (this.details.reservationId) request = reservationService.updateTimes(this.details.reservationId, times);
      else if (this.details.specialOfferId)
        request = specialOfferService.updateTimes(this.details.specialOfferId, times);
      else if (this.details.inquiryId) request = inquiryService.updateTimes(this.details.inquiryId, times);
      else return;

      request
        .then((details) => {
          details.threadId = this.thread.id;
          this.$emit('update', details);
          this.$refs.editTimesDialog.cancel();
          this.success('Heures mises à jour');
        })
        .catch((e) => {
          this.$refs.saveTimesBtn.init();
          this.addError('Erreur lors de la mise à jour des heures : ' + e);
        });
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4" :class="{'rounded-lg border border-grey-100 p-4': !isReservationConfirmed}">
    <router-link
      target="_blank"
      v-if="isReservation && isGasGav"
      :to="{name: 'reservationPage', params: {reservationId: details.reservationId}}"
      class="p-2 pl-3 flex justify-between bg-secondary-50 rounded-md items-center focus:bg-secondary-100 hover:bg-secondary-100">
      <span class="flex items-center gap-1.5 text-primary font-medium">RÉSERVATION</span>
      <i class="ga-icon ga-long_arrow_up text-xl text-primary rotate-45"></i>
    </router-link>

    <div v-if="!isReservationConfirmed" class="flex gap-3 items-center">
      <div
        class="w-[50px] h-[50px] flex items-center justify-center rounded-[10px]"
        :class="{
          'bg-amber-100': isInquiry || isInquiryExpired || isInquiryDeclined,
          'bg-secondary-50':
            isReservationQuery ||
            isSpecialOfferActive ||
            isReservationExpired ||
            isReservationRetracted ||
            isReservationDenied ||
            isSpecialOfferVoided,
          'bg-red-100': isReservationCancelled,
        }">
        <i
          class="ga-icon text-xl"
          :class="{
            'ga-information text-[#e08f0e]': isInquiry,
            'ga-clock text-[#e08f0e]': isInquiryExpired,
            'ga-supprimer text-[#e08f0e]': isInquiryDeclined,
            'ga-calendar text-secondary': isReservationQuery,
            'ga-clock text-secondary': isReservationExpired || isSpecialOfferExpired(details.expiry),
            'ga-supprimer text-secondary':
              isReservationRetracted ||
              isReservationDenied ||
              (!isSpecialOfferExpired(details.expiry) && isSpecialOfferVoided),
            'ga-coins-euro text-secondary': isSpecialOfferActive,
            'ga-supprimer text-red-500': isReservationCancelled,
          }"></i>
      </div>
      <div class="flex flex-col gap-0.5">
        <span class="text-grey-500 leading-[17px]">
          <template v-if="isInquiry || isReservationQuery">Action demandée</template>
          <template v-else-if="isSpecialOfferActive">En attente de réponse</template>
          <template v-else-if="isReservationDenied || isInquiryDeclined">Votre réponse</template>
          <template v-else-if="isReservation">Statut de la réservation</template>
          <template v-else-if="isReservationQueryInactive">Statut de la demande de réservation</template>
          <template v-else-if="isSpecialOfferActive || isSpecialOfferExpired(details.expiry) || isSpecialOfferVoided">
            Statut de l'offre spéciale
          </template>
          <template v-else>Statut de la demande</template>
        </span>
        <span class="text-md font-semibold text-grey-900 leading-[19px]">
          <template v-if="isInquiry">Demande d'information</template>
          <template v-else-if="isReservationQuery">Demande de réservation</template>
          <template v-else-if="isSpecialOfferActive">Offre envoyée</template>
          <template
            v-else-if="
              isReservationCancelled ||
              isReservationRetracted ||
              (!isSpecialOfferExpired(details.expiry) && isSpecialOfferVoided)
            ">
            Annulée
          </template>
          <template v-else-if="isReservationExpired || isInquiryExpired || isSpecialOfferExpired(details.expiry)">
            Expirée
          </template>
          <template v-else-if="isReservationDenied || isInquiryDeclined">Refusée</template>
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex justify-between">
        <span class="text-grey-500 bg-grey-50 rounded-full px-2.5 py-1 w-fit font-medium text-sm">
          {{ details.nbNights }} nuit{{ details.nbNights > 1 ? 's' : '' }}
        </span>
        <span
          v-tooltip.bottom="formatDateExpiry"
          v-if="timeLeft && !isSpecialOfferActive"
          class="text-white bg-red leading-[18px] rounded-full pl-2 pr-2.5 py-0.5 w-fit font-medium text-sm flex gap-1 items-center select-none">
          <i class="ga-icon ga-clock text-md" />
          {{ timeLeft }} restantes
        </span>
        <span
          v-else-if="timeLeft && isSpecialOfferActive"
          v-tooltip.bottom="formatDateExpiry"
          class="text-secondary bg-secondary-50 leading-[18px] rounded-full pl-2 pr-2.5 py-0.5 w-fit font-medium text-sm flex gap-1 items-center select-none">
          <i class="ga-icon ga-clock text-md" />
          Expire dans {{ timeLeft }}
        </span>
      </div>
      <div class="flex justify-between" :class="isDisabled ? 'text-grey' : 'text-grey-900'">
        <div class="flex flex-col gap-1">
          <span class="font-bold text-md">{{ resaDatesFormatted.checkin }}</span>
          <span :class="isDisabled ? 'text-grey' : 'text-grey-500'">{{ displayTime(details.dateCheckin) }}</span>
        </div>
        <div class="flex flex-col gap-1 items-end">
          <span class="font-bold text-md">{{ resaDatesFormatted.checkout }}</span>
          <span :class="isDisabled ? 'text-grey' : 'text-grey-500'">{{ displayTime(details.dateCheckout) }}</span>
        </div>
      </div>
      <span
        v-if="canEditTimes"
        class="flex py-2 px-3 bg-grey-50 rounded-md gap-1 items-center text-grey-900 font-medium hover:bg-grey-100 cursor-pointer select-none"
        @click="openEditTimes">
        <i class="ga-icon ga-clock text-md -translate-y-[1px]" />
        Modifier les heures
        <i class="ga-icon ga-chevron_right ml-auto text-grey-500 text-md" />
      </span>
      <div v-if="details.prevReservationCheckout || details.nextReservationCheckin" class="flex justify-between">
        <span v-if="details.prevReservationCheckout" class="text-sm text-grey-500" :class="{'!text-grey': isDisabled}">
          Départ précédent
          <br />
          <span class="font-medium text-grey-800" :class="{'!text-grey': isDisabled}">
            {{ formatAdjacentReservationDateTime(details.prevReservationCheckout) }}
          </span>
        </span>
        <span v-else />
        <span
          class="text-sm text-grey-500 text-right"
          :class="{'!text-grey': isDisabled}"
          v-if="details.nextReservationCheckin">
          Arrivée suivante
          <br />
          <span class="font-medium text-grey-800" :class="{'!text-grey': isDisabled}">
            {{ formatAdjacentReservationDateTime(details.nextReservationCheckin) }}
          </span>
        </span>
      </div>

      <div class="grid grid-cols-3 gap-2" :class="isDisabled ? 'text-grey' : 'text-grey-900'">
        <div class="rounded-md h-12 border border-grey-100 flex justify-between items-center p-3 gap-1">
          <span class="text-grey-500 truncate">Adultes</span>
          <span class="text-md font-bold">{{ details.nbAdults || 0 }}</span>
        </div>
        <div
          class="rounded-md h-12 border border-grey-100 flex justify-between items-center p-3 gap-1"
          v-tooltip.bottom="`Enfants : ${details.nbChildren || 0} / Bébés : ${details.nbBabies || 0}`">
          <span class="text-grey-500 truncate">Enfants</span>
          <span class="text-md font-bold">{{ nbChildrenAndBabies }}</span>
        </div>
        <div class="rounded-md h-12 border border-grey-100 flex justify-between items-center p-3 gap-1">
          <span class="text-grey-500 truncate">Animaux</span>
          <span class="text-md font-bold">{{ details.nbPets || 0 }}</span>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex gap-1.5 items-center" :class="isDisabled ? 'text-grey' : 'text-grey-900'">
        <span class="leading-[18px] text-md font-bold mr-auto">Montant total</span>
        <span
          v-if="isReservation || isReservationQuery"
          class="rounded-full px-2 py-0.5 text-sm font-medium leading-[18px]"
          :class="details.paymentPending ? 'text-grey-500 bg-grey-100' : 'text-green-500 bg-green-100'">
          {{ details.paymentPending ? 'En attente' : 'Payé' }}
        </span>
        <span class="leading-[18px] text-md font-bold">{{ formatAmountVirgule(details.total) }} €</span>
      </div>

      <template v-if="isReservation || isReservationQuery || isReservationQueryInactive">
        <div class="grid">
          <button
            class="place-self-end w-fit flex gap-1.5 items-center cursor-pointer text-grey-500 hover:text-grey-600 focus-visible:text-grey-700"
            @click="priceDetailsOpen = !priceDetailsOpen">
            <span class="select-none leading-[160%]">{{ priceDetailsOpen ? 'Masquer' : 'Afficher' }} les détails</span>
            <i class="ga-icon text-md ga-chevron_down" :class="{'rotate-180': priceDetailsOpen}" />
          </button>
          <div class="grid priceDetailsContainer" :class="{open: priceDetailsOpen}">
            <div
              class="grid grid-cols-[1fr_auto] gap-y-0.5 gap-x-4 [&>span]:text-grey-500 [&>span]:truncate [&>span:nth-child(even)]:text-right overflow-hidden">
              <span>Montant des nuits</span>
              <span>{{ details.nightsPrice ? formatAmountVirgule(details.nightsPrice) : 0 }} €</span>
              <span>Montant de la commission de la plateforme</span>
              <span>{{ details.commissionFees ? formatAmountVirgule(details.commissionFees) : 0 }} €</span>
              <span>Montant des frais de ménage</span>
              <span>{{ details.cleaningFees ? formatAmountVirgule(details.cleaningFees) : 0 }} €</span>
              <span>Montant de la taxe de séjour</span>
              <span>{{ details.tdsFees ? formatAmountVirgule(details.tdsFees) : 0 }} €</span>
            </div>
          </div>
        </div>
        <a tabindex="-1" v-if="isReservationConfirmed" :href="details.boardingPass" target="_blank" class="w-full">
          <AkButtonActionV2
            label="Consulter le livret d'accueil"
            class="!h-12 bg-grey-50 text-primary font-medium gap-1.5 hover:bg-grey-100 mt-3 w-full focus-visible:bg-grey-100">
            <template #iconLeft>
              <i class="ga-icon ga-eye text-3xl" />
            </template>
          </AkButtonActionV2>
        </a>
      </template>

      <template v-if="canAct">
        <div class="flex flex-wrap gap-2" v-if="isInquiryLive">
          <AkButtonValidationV2
            ref="preapproveBtn"
            @click="preapprove"
            label="Préapprouver la demande"
            class="font-medium w-full !h-12 gap-1.5 focus-visible:bg-primary-700">
            <template #iconLeft><i class="ga-icon ga-check text-3xl" /></template>
          </AkButtonValidationV2>
          <AkButtonActionV2
            label="Refuser"
            disabled
            title="Rendez-vous sur l'interface de Airbnb pour refuser une demande d'informations"
            class="!h-12 font-medium w-fit !px-8"
            btn-class="bg-grey-50 rounded-md text-grey" />
          <AkButtonActionV2
            ref="inquiryToSpecialOfferBtn"
            @click="initSpecialOfferFromInquiry"
            label="Faire une offre"
            class="!h-12 font-medium grow focus-visible:bg-secondary-100"
            btn-class="btn-secondary" />
        </div>

        <AkButtonActionV2
          v-else-if="isInquiryDeclined"
          ref="inquiryToSpecialOfferBtn"
          @click="initSpecialOfferFromInquiry"
          label="Faire une offre"
          class="!h-12 font-medium focus-visible:bg-secondary-100"
          btn-class="btn-secondary" />

        <div class="grid gap-2" v-else-if="isReservationQueryLive">
          <AkButtonValidationV2
            @click="initApproveReservationQuery"
            ref="resaQueryApproveBtn"
            label="Approuver la demande"
            class="font-medium !h-12 gap-1.5 focus-visible:bg-primary-700"></AkButtonValidationV2>
          <AkButtonActionV2
            @click="initReservationQueryDeny"
            label="Refuser la demande"
            class="!h-12 font-medium"
            btn-class="bg-grey-50 hover:bg-grey-100/70 focus-visible:bg-grey-100/70 rounded-md text-primary" />
        </div>
        <AkButtonValidationV2
          v-else-if="isSpecialOfferActiveLive"
          ref="withdrawSpecialOfferBtn"
          @click="withdrawSpecialOffer"
          label="Annuler l'offre"
          class="!h-12 font-medium"
          btn-class="bg-grey-50 hover:bg-grey-100/70 focus-visible:bg-grey-100/70 rounded-md text-primary" />
        <AkButtonActionV2
          v-else-if="isLiveReservationExpired || isLiveInquiryExpired || isLiveSpecialOfferExpired"
          label="Demande expirée"
          disabled
          class="!h-12 font-medium"
          btn-class="bg-grey-50 rounded-md text-grey-300" />
      </template>
      <div
        v-else-if="isInquiryLive || isReservationQueryLive || isSpecialOfferActiveLive"
        class="!h-12 font-medium flex items-center justify-center bg-grey-50 rounded-md text-grey text-center px-4">
        Vous n'êtes pas habilité à agir sur cette demande
      </div>

      <span
        v-if="isReservationQueryLive || isInquiryLive || isSpecialOfferActiveLive"
        class="text-sm text-grey-500 text-center">
        Réponse synchronisée avec Airbnb. Le voyageur sera notifié immédiatement.
      </span>

      <AkButtonActionV2
        v-else-if="isReservationRetracted || isReservationDenied || isReservationCancelled || isSpecialOfferVoided"
        :label="isReservationDenied ? 'Demande refusée' : 'Demande annulée'"
        disabled
        class="!h-12 font-medium"
        btn-class="bg-grey-50 rounded-md text-grey-300" />
    </div>
  </div>

  <AkDialogHeadless
    ref="denyReservationQueryDialog"
    :show-validate-button="false"
    :show-cancel-button="false"
    content-class="!p-3 !pb-4"
    width="480px"
    no-header>
    <div class="flex flex-col gap-4">
      <div class="flex gap-4 text-grey-900 items-center">
        <template v-if="reservationQueryDenyStep !== 4">
          <button
            @click="reservationQueryDenyBack"
            class="border border-grey-100 flex items-center justify-center h-[38px] aspect-square rounded-md hover:bg-grey-50">
            <i class="ga-icon ga-chevron_left text-3xl" />
          </button>
          <span class="font-medium">REFUSER LA DEMANDE</span>
        </template>
        <button
          @click="closeReservationQueryDeny"
          class="flex items-center justify-center h-[38px] aspect-square ml-auto group">
          <i class="ga-icon ga-supprimer text-3xl text-grey-500 group-hover:text-grey-700" />
        </button>
      </div>

      <div v-if="reservationQueryDenyStep === 1" class="flex flex-col gap-4 px-4">
        <div class="flex flex-col gap-1.5">
          <span class="font-bold text-md text-grey-900">Quel est le motif de votre refus ?</span>
          <span class="text-grey-500 font-medium">Cela nous permettra d’améliorer nos services</span>
        </div>
        <AkOptionSelect
          :options="reservationQueryDenyOptions"
          v-model="denyReasons.key"
          :validator="v$.denyReasons.key"
          @update:modelValue="nextReservationQueryDenyStep" />
      </div>

      <div v-if="reservationQueryDenyStep === 2" class="flex flex-col gap-4 px-4">
        <div class="flex flex-col gap-1.5">
          <span class="font-bold text-md text-grey-900">Votre message pour Airbnb</span>
          <span class="text-grey-500 font-medium">Cela nous permettra d’améliorer nos services</span>
        </div>
        <PermanentMessage :icon="false" description="Visible uniquement par Airbnb, pas par le voyageur">
          <template #icon>
            <i class="ga-icon ga-information text-lg text-grey-900" />
          </template>
        </PermanentMessage>
        <AkInputTextArea
          :displayLabel="false"
          v-model="denyReasons.otaMessage"
          :disabled="!isGasGav"
          label="Obligatoire"
          :validator="v$.denyReasons.otaMessage" />
        <AkButtonActionV2
          :class="{disabled: v$.denyReasons.otaMessage.$invalid}"
          label="Suivant"
          @click="nextReservationQueryDenyStepFromMessage"
          class="self-end !px-6 font-medium !h-10" />
      </div>

      <div v-if="reservationQueryDenyStep === 3" class="flex flex-col gap-4 px-4">
        <div class="flex flex-col gap-1.5">
          <span class="font-bold text-md text-grey-900">Message pour le voyageur</span>
          <span class="text-grey-500 font-medium">Pourquoi avez-vous refusé sa demande ?</span>
        </div>
        <AkInputTextArea
          :displayLabel="false"
          v-model="denyReasons.guestMessage"
          :validator="v$.denyReasons.guestMessage"
          :disabled="!isGasGav"
          label="Obligatoire" />
        <AkButtonValidationV2
          ref="denyReservationQuerySubmitBtn"
          :class="{disabled: v$.denyReasons.guestMessage.$invalid}"
          label="Suivant"
          @click="denyReservationQuery"
          class="self-end !px-6 font-medium !h-10" />
      </div>

      <div v-if="reservationQueryDenyStep === 4" class="flex flex-col gap-4 px-4 py-4 items-center">
        <div class="bg-red-50 rounded-full p-2 w-fit">
          <i class="ga-icon ga-close_circle text-red text-[48px]" />
        </div>
        <div class="flex flex-col gap-1.5">
          <span class="font-bold text-md text-grey-900 text-center">Demande refusée</span>
          <span class="text-grey-500 font-medium text-center">Airbnb et le voyageur ont reçu votre message.</span>
        </div>
      </div>
    </div>
  </AkDialogHeadless>

  <AkDialogHeadless
    ref="specialOfferDialog"
    :show-validate-button="false"
    :show-cancel-button="false"
    content-class="!p-6 !pb-8"
    width="480px"
    no-header>
    <div class="flex flex-col gap-[28px]">
      <div class="flex gap-4 text-grey-900 items-center">
        <template v-if="specialOfferStep !== 2">
          <button
            @click="closeSpecialOfferDialog"
            class="border border-grey-100 flex items-center justify-center h-[38px] aspect-square rounded-md hover:bg-grey-50">
            <i class="ga-icon ga-chevron_left text-3xl" />
          </button>
          <span class="font-medium">FAIRE UNE OFFRE SPÉCIALE</span>
        </template>
        <button
          @click="closeSpecialOfferDialog"
          class="flex items-center justify-center h-[38px] aspect-square ml-auto group">
          <i class="ga-icon ga-supprimer text-3xl text-grey-500 group-hover:text-grey-700" />
        </button>
      </div>

      <div class="flex h-[492px] items-center justify-center" v-if="specialOfferLoading">
        <Spinner />
      </div>

      <div v-else-if="specialOfferStep === 1" class="flex flex-col gap-4">
        <div class="flex flex-col gap-1.5">
          <span class="font-bold text-md text-grey-900">
            Envoyer une offre spéciale pour {{ accommodationName }} à {{ clientName }}
          </span>
          <span class="text-grey-500">
            Le voyageur aura 24H pour réserver. Vos dates restent ouvertes pour d’autre voyageurs jusqu’à la
            confirmation.
          </span>
        </div>
        <div class="flex flex-col gap-4">
          <AkCalendar
            v-model="specialOfferToCreate.dateRange"
            hide-on-range-selection
            selection="range"
            label="Dates"
            :placeholder="specialOfferToCreate.datesPlaceholder"
            :disabled-dates="specialOfferToCreate.datesUnavailable"
            hide-required
            :validator="v$.specialOfferToCreate.dateRange" />
          <AkDropdown
            :options="specialOfferNbGuestOptions"
            v-model="specialOfferToCreate.nbGuest"
            label="Nombre de voyageurs"
            hide-required
            :validator="v$.specialOfferToCreate.nbGuest" />
          <AkInputNumber
            v-model="specialOfferToCreate.totalPrice"
            label="Prix"
            suffix=" €"
            hide-required
            :min="0"
            :validator="v$.specialOfferToCreate.totalPrice" />
        </div>
        <AkButtonValidationV2
          label="Envoyer l'offre"
          :disabled="v$.specialOfferToCreate.$invalid"
          @click="sendSpecialOffer"
          class="self-end !px-6 font-medium !h-10" />
      </div>

      <div v-if="specialOfferStep === 2" class="flex flex-col gap-4 px-4 py-4 items-center">
        <div class="bg-secondary rounded-full h-16 w-16 flex justify-center items-center">
          <i class="ga-icon ga-check text-white text-[32px]" />
        </div>
        <div class="flex flex-col gap-1.5">
          <span class="font-bold text-[18px] text-grey-900 text-center">Offre spéciale envoyée !</span>
          <span class="text-grey-500 font-medium text-center">
            La proposition à été envoyée et le
            <br />
            voyageur à été informé sur Airbnb.
          </span>
        </div>
      </div>
    </div>
  </AkDialogHeadless>

  <AkDialogConfirm ref="confirmReservationQueryDialog" label="Confirmer l'acceptation ?" />

  <AkDialogHeadless
    ref="editTimesDialog"
    :show-validate-button="false"
    :show-cancel-button="false"
    content-class="!p-4"
    width="380px"
    no-header>
    <div class="flex flex-col gap-8">
      <div class="flex gap-4 text-grey-900 items-center">
        <span class="font-medium">MODIFIER LES HEURES</span>
        <button
          @click="$refs.editTimesDialog.cancel()"
          class="flex items-center justify-center h-[38px] aspect-square ml-auto group">
          <i class="ga-icon ga-supprimer text-3xl text-grey-500 group-hover:text-grey-700" />
        </button>
      </div>
      <PermanentMessage
        :icon="false"
        description="Ces heures sont uniquement informatives et ne sont pas transmises à Airbnb">
        <template #icon>
          <i class="ga-icon ga-information text-lg text-grey-900" />
        </template>
      </PermanentMessage>
      <div class="flex flex-col gap-4">
        <AkTime v-model="timesToEdit.checkin" label="Heure d'arrivée" :min-date="checkinMinDate" />
        <AkTime v-model="timesToEdit.checkout" label="Heure de départ" :max-date="checkoutMaxDate" />
      </div>
      <AkButtonValidationV2
        ref="saveTimesBtn"
        label="Enregistrer"
        @click="saveTimes"
        class="self-end !px-6 font-medium !h-10" />
    </div>
  </AkDialogHeadless>
</template>

<style scoped>
.priceDetailsContainer {
  grid-template-rows: 0fr;
  padding: 0;
  transition:
    grid-template-rows 0.3s ease,
    padding 0.3s ease;

  &.open {
    grid-template-rows: 1fr;
    @apply pt-2;
  }
}
</style>
